import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiDecryptService } from 'src/app/_services/pii-decrypt.service';
import { PiiAuditService, PiiAuditRequest } from 'src/app/_services/pii-audit.service';
import { PiiUnhideModalComponent } from '../pii-unhide-modal/pii-unhide-modal.component';
import { AlertService } from 'src/app/_services/alert.service';
import { StorageService } from 'src/app/_services/storage.service';

export interface PiiTableColumn {
  key: string;
  label: string;
  isPii: boolean;
  visibleStart?: number;
  visibleEnd?: number;
  width?: string;
}

export interface PiiTableRow {
  id: string | number;
  data: { [key: string]: any };
  isUnmasked?: boolean;
  isLoading?: boolean;
}

@Component({
  selector: 'app-pii-table',
  templateUrl: './pii-table.component.html',
  styleUrls: ['./pii-table.component.scss']
})
export class PiiTableComponent implements OnInit, OnChanges {
  @Input() columns: PiiTableColumn[] = [];
  @Input() data: any[] = [];
  @Input() userIdField: string = 'userId'; // Field name that contains the user ID
  @Input() showActions: boolean = true;
  @Input() tableClass: string = 'table table-striped table-bordered';
  @Input() loading: boolean = false;

  processedRows: PiiTableRow[] = [];

  constructor(
    private modalService: NgbModal,
    private piiDecryptService: PiiDecryptService,
    private piiAuditService: PiiAuditService,
    private alertService: AlertService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(): void {
    this.processData();
  }

  /**
   * Process the input data and decrypt/mask PII fields
   */
  private processData(): void {
    this.processedRows = this.data.map((row, index) => {
      const processedData: { [key: string]: any } = {};
      
      this.columns.forEach(column => {
        const value = row[column.key];
        
        if (column.isPii && value) {
          // Decrypt and create masked version for PII fields
          const decrypted = this.piiDecryptService.decrypt(value.toString());
          const masked = this.piiDecryptService.getMaskedData(
            decrypted, 
            column.visibleStart || 3, 
            column.visibleEnd || 1
          );
          
          processedData[column.key] = {
            original: value,
            decrypted: decrypted,
            masked: masked,
            isPii: true
          };
        } else {
          // Regular non-PII field
          processedData[column.key] = {
            original: value,
            decrypted: value,
            masked: value,
            isPii: false
          };
        }
      });

      return {
        id: row.id || index,
        data: processedData,
        isUnmasked: false,
        isLoading: false
      };
    });
  }

  /**
   * Handle unmask button click for a specific row
   */
  onUnmaskRow(rowIndex: number): void {
    const row = this.processedRows[rowIndex];
    if (!row || row.isLoading) return;

    const userId = this.data[rowIndex][this.userIdField];
    if (!userId) {
      this.alertService.toastErrorMessageAlert({
        title: 'User ID not found'
      });
      return;
    }

    const modalRef = this.modalService.open(PiiUnhideModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });

    modalRef.componentInstance.fieldName = 'table_row_' + row.id;
    modalRef.componentInstance.userId = userId;

    modalRef.result.then((result) => {
      if (result) {
        this.processUnmaskRequest(rowIndex, result, userId);
      }
    }).catch(() => {
      // Modal dismissed
    });
  }

  /**
   * Process the unmask request with audit logging
   */
  private processUnmaskRequest(rowIndex: number, auditData: any, userId: string): void {
    const row = this.processedRows[rowIndex];
    row.isLoading = true;
    
    const currentUser = this.storageService.getUser();
    const piiFields = this.columns.filter(col => col.isPii).map(col => col.key);
    
    const auditRequest: PiiAuditRequest = {
      userId: userId,
      fieldName: `table_row_${row.id}_fields_${piiFields.join(',')}`,
      reasonId: auditData.reasonId,
      comment: auditData.comment,
      accessedBy: currentUser?.userKey || 'unknown',
      timestamp: new Date().toISOString()
    };

    // Log the audit first
    this.piiAuditService.logPiiAccess(auditRequest).subscribe({
      next: (response) => {
        if (response.success) {
          // Unmask the row
          row.isUnmasked = true;
          row.isLoading = false;
          
          this.alertService.toastSuccessMessageAlert({
            title: 'PII Data Unmasked'
          });
        } else {
          row.isLoading = false;
          this.alertService.toastErrorMessageAlert({
            title: 'Audit Logging Failed'
          });
        }
      },
      error: (error) => {
        row.isLoading = false;
        this.alertService.toastErrorMessageAlert({
          title: 'Error'
        });
      }
    });
  }

  /**
   * Mask a specific row
   */
  onMaskRow(rowIndex: number): void {
    const row = this.processedRows[rowIndex];
    if (row) {
      row.isUnmasked = false;
    }
  }

  /**
   * Copy all PII data from a row to clipboard
   */
  onCopyRow(rowIndex: number): void {
    const row = this.processedRows[rowIndex];
    if (!row || !row.isUnmasked) return;

    const piiData: string[] = [];
    this.columns.forEach(column => {
      if (column.isPii && row.data[column.key]) {
        piiData.push(`${column.label}: ${row.data[column.key].decrypted}`);
      }
    });

    const textToCopy = piiData.join('\n');
    
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        this.alertService.toastSuccessMessageAlert({
          title: 'PII Data Copied'
        });
      }).catch(() => {
        this.alertService.toastErrorMessageAlert({
          title: 'Copy Failed'
        });
      });
    }
  }

  /**
   * Get display value for a cell
   */
  getCellValue(row: PiiTableRow, columnKey: string): string {
    const cellData = row.data[columnKey];
    if (!cellData) return '';
    
    if (cellData.isPii) {
      return row.isUnmasked ? cellData.decrypted : cellData.masked;
    }
    
    return cellData.original || '';
  }

  /**
   * Check if row has any PII data
   */
  hasRowPiiData(row: PiiTableRow): boolean {
    return this.columns.some(col => col.isPii && row.data[col.key]?.original);
  }

  /**
   * Get count of unmasked rows
   */
  get unmaskedRowsCount(): number {
    return this.processedRows.filter(row => row.isUnmasked).length;
  }

  /**
   * Mask all rows
   */
  maskAllRows(): void {
    this.processedRows.forEach(row => {
      row.isUnmasked = false;
    });
  }
} 